import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiCloud,
  FiArrowRight,
  FiDollarSign,
  FiShield,
  FiCpu,
  FiActivity,
  FiDatabase,
  FiTrendingUp,
  FiCheckCircle,
  FiCheck
} from 'react-icons/fi';

/**
 * Informational page shown after trial signup
 * Explains benefits of connecting AWS account before redirecting to configuration
 */
const ConnectAwsInfoPage: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: FiDollarSign,
      title: 'Cloud Financial Operations',
      subtitle: '(FinOps)',
      bullets: [
        'Real-time cost tracking across all AWS services',
        'Anomaly detection: Get alerted when spending deviates from normal patterns',
        'Cost optimization: AI-powered recommendations to reduce waste (idle resources, right-sizing, Reserved Instances)',
        'Budget forecasting with burn rate analysis',
        'Multi-account cost allocation and chargeback'
      ]
    },
    {
      icon: FiShield,
      title: 'Cloud Security & Compliance',
      subtitle: '(Guardrails)',
      bullets: [
        'Automated policy scanning across your infrastructure',
        'Resource tagging compliance tracking (identify untagged resources costing you visibility)',
        'Security posture assessment (IAM, S3, encryption, logging)',
        'Violation tracking with remediation workflows',
        'Compliance reporting across accounts'
      ]
    },
    {
      icon: FiCpu,
      title: 'GenAI & LLM Operations',
      subtitle: '(GenAIOps)',
      bullets: [
        'Monitor every LLM call: prompts, completions, tokens, costs, latency',
        'Prompt versioning with environment promotion (dev → staging → production)',
        'Bedrock integration: Sync prompts between Aivilo and AWS Bedrock Prompt Management',
        'Vector storage: Export and manage embeddings in S3 Vectors',
        'Model cost analytics: Track spending by provider, model, and user',
        'Session tracking: Group LLM calls into conversations'
      ]
    },
    {
      icon: FiActivity,
      title: 'AI Agent Operations',
      subtitle: '(AgentOps)',
      bullets: [
        'Agent run observability with step-by-step execution logs',
        'Safety monitoring: Detect and alert on agent policy violations',
        'Tool usage tracking: See which tools your agents invoke',
        'Performance metrics: Latency, success rates, retry patterns',
        'Incident management for failed or problematic runs'
      ]
    },
    {
      icon: FiDatabase,
      title: 'Data Pipeline Operations',
      subtitle: '(DataOps)',
      bullets: [
        'Data catalog with automatic lineage discovery',
        'Column-level lineage: Understand field-level transformations',
        'Data quality monitoring and alerting',
        'Pipeline run history and failure analysis',
        'Governance: Track ownership, classifications, and compliance'
      ]
    },
    {
      icon: FiTrendingUp,
      title: 'Your Transformation Journey,',
      subtitle: 'Tracked',
      bullets: [
        'Manage AI/cloud transformation projects with milestones and timelines',
        'Budget vs. actual spend visibility',
        'Team collaboration with role-based access',
        'Jira integration to sync with your existing workflows',
        'Complete audit trail of your progress'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center mb-4">
            <FiCheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Welcome to Your 60-Day Trial!
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Your trial has been activated. Now let's connect your AWS account to unlock the full power of Aivilo Lens.
          </p>
        </div>

        {/* Next Step Banner */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mb-8">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <FiCloud className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Next Step: Connect Your AWS Account
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                To access CloudOps features including cost management, security guardrails, and infrastructure monitoring,
                you'll need to connect your AWS account. This allows Aivilo Lens to securely access your cloud infrastructure data.
              </p>
              <button
                onClick={() => navigate('/hub/aws-environments')}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Configure AWS Account
                <FiArrowRight className="ml-2 w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="mb-12">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 text-center">
            What You'll Unlock with AWS Integration
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.title}
                  className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col"
                >
                  <div className="flex items-start space-x-4 mb-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
                      <Icon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-base text-gray-900 dark:text-white leading-tight">
                        {feature.title}
                        {feature.subtitle && (
                          <>
                            <br />
                            <span>{feature.subtitle}</span>
                          </>
                        )}
                      </h3>
                    </div>
                  </div>
                  <div className="space-y-2.5 pl-1">
                    {feature.bullets.map((bullet) => (
                      <div key={bullet} className="flex items-start gap-2.5">
                        <FiCheck className="w-4 h-4 text-green-500 dark:text-green-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                          {bullet}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Security Note */}
        <div className="bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 mb-8">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <FiShield className="w-6 h-6 text-gray-600 dark:text-gray-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
                Secure AWS Integration - How It Works
              </h3>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                Aivilo Lens connects to your AWS account using cross-account IAM roles. Here's what we access and why:
              </p>

              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">What We Access:</h4>
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1 list-disc list-inside">
                  <li>Cost & Usage data (for FinOps insights and budget tracking)</li>
                  <li>Resource metadata (EC2, RDS, Lambda, S3, etc. - for inventory and compliance)</li>
                  <li>Tags (for cost allocation and compliance scoring)</li>
                  <li>AI/ML resources (SageMaker, Bedrock - for AI operations monitoring)</li>
                  <li>IAM policies (for security posture assessment)</li>
                </ul>
              </div>

              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">What We NEVER Access:</h4>
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1 list-disc list-inside">
                  <li>Your application data or S3 object contents</li>
                  <li>Database records or file contents</li>
                  <li>Secrets or credentials</li>
                </ul>
              </div>

              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">GenAI-Specific Features:</h4>
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1 list-disc list-inside">
                  <li>S3 Vectors for embedding storage</li>
                  <li>Bedrock Prompt Management and Evaluations</li>
                </ul>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">You're Always in Control:</h4>
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1 list-disc list-inside">
                  <li>Revoke access anytime by deleting the IAM role</li>
                  <li>External ID validation prevents unauthorized access</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Help Section */}
        <div className="text-center text-gray-600 dark:text-gray-400">
          <p>
            Need help setting up? Contact us at{' '}
            <a href="mailto:support@aivilo.ai" className="text-blue-600 dark:text-blue-400 hover:underline">
              support@aivilo.ai
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ConnectAwsInfoPage;
